import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { MetricRow, type MetricSpec } from '@/components/cards'
import { DebtRow, List } from '@/components/rows'
import { EmptyState, ErrorState, LoadingRegion, SkeletonList, SkeletonMetrics } from '@/components/states'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { formatBasisPoints, formatMoney, formatPercent } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/** Balances, rates and utilization across credit cards and loans. */
export function DebtScreen() {
  const navigate = useNavigate()
  const debt = useAsync(() => api.analysis.debt(), [])
  const recommendations = useAsync(() => api.recommendations.list({ theme: 'debt' }), [])

  const data = debt.state.data
  const accounts = data?.accounts ?? []
  const topRecommendation = recommendations.state.data?.find((item) => item.status === 'open')

  const screenState =
    debt.state.status === 'error'
      ? 'error'
      : debt.state.status === 'loading' && !data
        ? 'loading'
        : accounts.length === 0 || (data?.totalOwed.amount ?? 0) === 0
          ? 'empty'
          : 'ready'

  const metrics: MetricSpec[] = data
    ? [
        { key: 'total', label: t('debt.metric.total'), value: formatMoney(data.totalOwed, { absolute: true }) },
        {
          key: 'interest',
          label: t('debt.metric.monthlyInterest'),
          value: formatMoney(data.monthlyInterest, { absolute: true }),
          tone: 'caution',
        },
        { key: 'utilization', label: t('debt.metric.utilization'), value: formatPercent(data.utilization) },
        {
          key: 'apr',
          label: t('debt.metric.averageApr'),
          value: formatBasisPoints(data.averageAprBasisPoints),
        },
      ]
    : []

  return (
    <Screen
      id="debt"
      state={screenState}
      actions={
        screenState === 'ready' ? (
          <>
            <Button variant="primary" onClick={() => navigate('debtPlanner')}>
              {t('debt.cta.plan')}
            </Button>
            {topRecommendation ? (
              <Button
                variant="secondary"
                onClick={() => navigate('recommendationDetail', { id: topRecommendation.id })}
              >
                {t('recommendationDetail.title')}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonMetrics />
          <SkeletonList rows={3} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="debt.error.title"
          error={debt.state.status === 'error' ? debt.state.error : undefined}
          onRetry={debt.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="debt.empty.title" />
      ) : (
        <div className="stack">
          <MetricRow metrics={metrics} />
          <List>
            {accounts.map((account) => (
              <DebtRow
                key={account.id}
                account={account}
                onSelect={() => navigate('accountDetail', { id: account.id })}
              />
            ))}
          </List>
        </div>
      )}
    </Screen>
  )
}
